/**
 * Conversation Panel Component
 * @implements FS-002.7 - AI PM Conversation
 * @implements FS-002.7.4 - Conversation History Persistence
 * @implements FS-002.7.5 - Conversation Continuity
 * @implements FS-002.7.6 - AI PM Conversation in Archived Projects
 * @implements FS-002.7.8 - Conversation Real-Time Updates
 * @implements FS-002.7.9 - Conversation Input Behavior
 * @implements FS-002.7.10 - Conversation Message Pagination
 * @implements FS-002.7.11 - AI PM Response Delivery
 * @implements US-020 - AI PM Conversation UI
 *
 * Main conversation panel that combines MessageList, MessageInput,
 * and integrates with the useConversation hook for state management.
 */

import { useRef } from 'react';
import { Box, Paper, Typography, Alert, CircularProgress } from '@mui/material';
import { SmartToy as AiIcon } from '@mui/icons-material';
import { MessageList, type MessageListRef } from './MessageList';
import { MessageInput } from './MessageInput';
import { useConversation } from '@/hooks/useConversation';

/**
 * Props for ConversationPanel component
 */
export interface ConversationPanelProps {
  /** Project ID for the conversation */
  projectId: string;
  /** Whether the project is archived */
  isProjectArchived?: boolean;
  /** Optional height for the panel */
  height?: string | number;
  /** Callback when user sends a message (for onboarding tracking) */
  onMessageSent?: () => void;
  /** Callback when AI PM responds (for onboarding tracking) */
  onAiPmResponse?: () => void;
}

/**
 * ConversationPanel Component
 * @implements FS-002.7 - AI PM Conversation
 *
 * Full conversation interface including:
 * - Message history with infinite scroll
 * - Message input with keyboard shortcuts
 * - Real-time updates via subscription
 * - Typing indicator for AI PM responses
 * - Read-only mode for archived projects
 *
 * @example
 * ```tsx
 * <ConversationPanel
 *   projectId="proj-123"
 *   isProjectArchived={false}
 *   height="calc(100vh - 200px)"
 * />
 * ```
 */
export const ConversationPanel = ({
  projectId,
  isProjectArchived = false,
  height = 500,
  onMessageSent,
  onAiPmResponse,
}: ConversationPanelProps) => {
  const messageListRef = useRef<MessageListRef>(null);

  const {
    messages,
    isLoading,
    isLoadingMore,
    isSending,
    isTyping,
    hasMore,
    isReadOnly,
    error,
    sendMessage,
    loadMoreMessages,
  } = useConversation({
    projectId,
    isProjectArchived,
    onMessageSent,
    onAiPmResponse,
  });

  /**
   * Handle sending a message
   * @implements FS-002.7.9 - Input is cleared after successful message send
   */
  const handleSend = async (content: string) => {
    await sendMessage(content);
    // Scroll to bottom after sending
    setTimeout(() => {
      messageListRef.current?.scrollToBottom(true);
    }, 100);
  };

  /**
   * Render loading state
   */
  if (isLoading && messages.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          height,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          height,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          p: 2,
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        height,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <AiIcon color="secondary" />
        <Typography variant="subtitle1" fontWeight="medium">
          AI Project Manager
        </Typography>
        {isReadOnly && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 'auto', fontStyle: 'italic' }}
          >
            Read-only
          </Typography>
        )}
      </Box>

      {/* Archived Project Notice */}
      {isProjectArchived && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          This project is archived. You can view the conversation history but
          cannot send new messages. Unarchive the project to continue the
          conversation.
        </Alert>
      )}

      {/* Message List */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <MessageList
          ref={messageListRef}
          messages={messages}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={loadMoreMessages}
          isTyping={isTyping}
          height="100%"
        />
      </Box>

      {/* Message Input */}
      <MessageInput
        projectId={projectId}
        onSend={handleSend}
        disabled={isReadOnly}
        disabledReason={
          isProjectArchived
            ? 'This project is archived. Unarchive to send messages.'
            : isReadOnly
            ? 'This conversation is read-only.'
            : undefined
        }
        isSending={isSending}
        placeholder="Ask the AI PM about your product..."
      />
    </Paper>
  );
};
